import { env } from '@/lib/env';
import type { ImageModel } from 'ai';
import Replicate from 'replicate';

const models = [
  'black-forest-labs/flux-1.1-pro',
  'black-forest-labs/flux-1.1-pro-ultra',
  'black-forest-labs/flux-dev',
  'black-forest-labs/flux-kontext-pro',
  'black-forest-labs/flux-kontext-max',
  'google/imagen-4',
  'google/nano-banana',
  'google/nano-banana-pro',
  'bytedance/seedream-4',
] as const;

type CreateJobParams = {
  client: Replicate;
  modelId: (typeof models)[number];
  prompt: string;
  size: `${string}x${string}` | undefined;
  seed: number | undefined;
  abortSignal: AbortSignal | undefined;
  headers: Record<string, string | undefined> | undefined;
  imagePrompt: string | string[] | undefined;
};

const createJob = async ({
  client,
  modelId,
  prompt,
  size,
  seed,
  abortSignal,
  headers,
  imagePrompt,
}: CreateJobParams) => {
  console.log('🔧 Setting up job parameters...');
  const [width, height] = size?.split('x').map(Number) ?? [1024, 1024];

  // Convert to smallest possible aspect ratio
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  const simplifiedW = width / divisor;
  const simplifiedH = height / divisor;

  const ratio = width / height;
  console.log(
    '📏 Calculated aspect ratio:',
    `${simplifiedW}:${simplifiedH}`,
    `(${ratio.toFixed(2)})`
  );

  if (ratio > 21 / 9 || ratio < 9 / 21) {
    console.log('❌ Invalid aspect ratio:', ratio);
    throw new Error('Aspect ratio must be between 21:9 and 9:21');
  }

  const baseInput = {
    prompt,
    width,
    height,
    seed,
    output_format: 'png',
    safety_tolerance: 2,
    ...(imagePrompt && {
      image_prompt: Array.isArray(imagePrompt) ? imagePrompt[0] : imagePrompt,
    }),
  };

  switch (modelId) {
    case 'black-forest-labs/flux-1.1-pro':
      console.log('🎯 Running FLUX-1.1-Pro model...');
      return await client.run(modelId, {
        input: {
          ...baseInput,
          output_format: 'png',
          prompt_upsampling: true,
        },
      });
    case 'black-forest-labs/flux-1.1-pro-ultra':
      console.log('⚡ Running FLUX-1.1-Pro-Ultra model...');
      return await client.run(modelId, {
        input: {
          ...baseInput,
          go_fast: true,
          num_outputs: 1,
          aspect_ratio: `${simplifiedW}:${simplifiedH}`,
          output_format: 'png',
          prompt_upsampling: true,
          raw: true,
          image_prompt_strength: 0.5,
          image_prompt: Array.isArray(imagePrompt)
            ? imagePrompt[0]
            : imagePrompt,
        },
      });
    case 'black-forest-labs/flux-dev':
      console.log('🎭 Running FLUX-Dev model...');
      return await client.run(modelId, {
        input: {
          prompt,
          prompt_upsampling: true,
          seed,
          aspect_ratio: `${simplifiedW}:${simplifiedH}`,
          output_format: 'png',
          go_fast: true,
          ...(imagePrompt && {
            input_image: Array.isArray(imagePrompt)
              ? imagePrompt[0]
              : imagePrompt,
          }),
        },
      });
    case 'black-forest-labs/flux-kontext-pro':
      console.log('🎪 Running FLUX-Kontext-Pro model...');
      return await client.run(modelId, {
        input: {
          prompt,
          prompt_upsampling: true,
          seed,
          aspect_ratio: `${simplifiedW}:${simplifiedH}`,
          output_format: 'png',
          safety_tolerance: 2,
          ...(imagePrompt && {
            input_image: Array.isArray(imagePrompt)
              ? imagePrompt[0]
              : imagePrompt,
          }),
        },
      });
    case 'black-forest-labs/flux-kontext-max':
      console.log('🎭 Running FLUX-Kontext-Max model...');
      return await client.run(modelId, {
        input: {
          prompt,
          prompt_upsampling: true,
          seed,
          aspect_ratio: `${simplifiedW}:${simplifiedH}`,
          output_format: 'png',
          safety_tolerance: 2,
          ...(imagePrompt && {
            input_image: Array.isArray(imagePrompt)
              ? imagePrompt[0]
              : imagePrompt,
          }),
        },
      });
    case 'google/imagen-4':
      console.log('🎭 Running Imagen 4 model...');
      return await client.run(modelId, {
        input: {
          prompt,
          aspect_ratio: `${simplifiedW}:${simplifiedH}`,
          safety_filter_level: 'block_medium_and_above',
          ...(imagePrompt && {
            input_image: Array.isArray(imagePrompt)
              ? imagePrompt[0]
              : imagePrompt,
          }),
        },
      });
    case 'google/nano-banana': {
      console.log('🎭 Running Nano Banana model...');

      // Handle both single image and multiple images
      let imageInputArray: string[] | undefined;
      if (imagePrompt) {
        if (Array.isArray(imagePrompt)) {
          imageInputArray = imagePrompt;
        } else {
          imageInputArray = [imagePrompt];
        }
      }

      console.log({
        input: {
          prompt,
          ...(imageInputArray && { image_input: imageInputArray }),
          output_format: 'png',
        },
      });
      return await client.run(modelId, {
        input: {
          prompt,
          ...(imageInputArray && { image_input: imageInputArray }),
          output_format: 'png',
        },
      });
    }
    case 'google/nano-banana-pro': {
      console.log('🎭 Running Nano Banana Pro model...');

      // Handle both single image and multiple images
      let imageInputArray: string[] | undefined;
      if (imagePrompt) {
        if (Array.isArray(imagePrompt)) {
          imageInputArray = imagePrompt;
        } else {
          imageInputArray = [imagePrompt];
        }
      }

      console.log({
        input: {
          prompt,
          ...(imageInputArray && { image_input: imageInputArray }),
          output_format: 'png',
        },
      });
      return await client.run(modelId, {
        input: {
          prompt,
          ...(imageInputArray && { image_input: imageInputArray }),
          resolution: '2K',
          output_format: 'png',
        },
      });
    }
    case 'bytedance/seedream-4': {
      console.log('🎭 Running Seedream 4 model...');

      // Handle both single image and multiple images
      let imageInputArray: string[] | undefined;
      if (imagePrompt) {
        if (Array.isArray(imagePrompt)) {
          imageInputArray = imagePrompt;
        } else {
          imageInputArray = [imagePrompt];
        }
      }

      return await client.run(modelId, {
        input: {
          prompt,
          size: '2K',
          aspect_ratio: `${simplifiedW}:${simplifiedH}`,
          width: width,
          height: height,
          ...(imageInputArray && { image_input: imageInputArray }),
        },
      });
    }
    default:
      console.log('❌ Unsupported model:', modelId);
      throw new Error(`Model ${modelId} not supported`);
  }
};

export const replicate = {
  image: (modelId: (typeof models)[number]): ImageModel => ({
    modelId,
    provider: 'replicate',
    specificationVersion: 'v1',
    maxImagesPerCall: 1,
    doGenerate: async ({
      prompt,
      providerOptions,
      seed,
      size,
      abortSignal,
      headers,
    }) => {
      console.log('🚀 Starting Replicate image generation...');
      console.log('🎨 Model:', modelId);
      console.log('📝 Prompt:', prompt);
      console.log('📐 Size:', size);
      console.log('🎲 Seed:', seed);

      if (!env.REPLICATE_API_KEY) {
        throw new Error('REPLICATE_API_KEY is not defined');
      }

      const replicateClient = new Replicate({
        auth: env.REPLICATE_API_KEY,
        useFileOutput: false,
      });

      let imagePrompt: string | string[] | undefined;

      console.log('🔍 Checking providerOptions:', providerOptions);
      if (providerOptions?.replicate?.image) {
        const imageData = providerOptions.replicate.image;
        if (typeof imageData === 'string') {
          imagePrompt = imageData;
        } else if (
          Array.isArray(imageData) &&
          imageData.every((item) => typeof item === 'string')
        ) {
          imagePrompt = imageData as string[];
        }
        if (Array.isArray(imagePrompt)) {
          console.log(
            `🖼️ Multiple images provided: ${imagePrompt.length} images`
          );
          console.log(
            '🖼️ First image:',
            `${imagePrompt[0]?.substring(0, 50)}...`
          );
        } else if (typeof imagePrompt === 'string') {
          console.log(
            '🖼️ Single image provided:',
            `${imagePrompt.substring(0, 50)}...`
          );
        }
      } else {
        console.log(
          '❌ No image prompt found in providerOptions.replicate.image'
        );
      }

      console.log('⚡ Creating job with Replicate...');
      const result = await createJob({
        client: replicateClient,
        modelId,
        prompt,
        size,
        seed,
        abortSignal,
        headers,
        imagePrompt,
      });

      if (!result) {
        console.log('❌ No image generated');
        throw new Error('No image generated');
      }

      console.log(result);
      console.log('✅ Job completed successfully!');

      // Get the first image URL from the result
      console.log('🔗 Image URL:', result);

      console.log('📥 Fetching generated image...');
      // Fetch the image data
      const imageResponse = await fetch(result as unknown as string);
      if (!imageResponse.ok) {
        console.log('❌ Failed to fetch image:', imageResponse.statusText);
        throw new Error(
          `Failed to fetch generated image: ${imageResponse.statusText}`
        );
      }

      const imageBuffer = await imageResponse.arrayBuffer();
      console.log('💾 Image buffer size:', imageBuffer.byteLength, 'bytes');

      console.log('🎉 Image generation completed successfully!');

      return {
        images: [new Uint8Array(imageBuffer)],
        warnings: [],
        response: {
          timestamp: new Date(),
          modelId,
          headers: undefined,
        },
      };
    },
  }),
};
